import {useState} from 'react'
import {Image, StyleSheet, Text, TouchableOpacity, View} from 'react-native'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayOptionTextStyle,
  usePlaySelectionMode,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {SLIDE_TYPE_META} from '#/types/capsule'

/** Capsule accent for this block type — the fallback outside a book. */
const ACCENT = SLIDE_ACCENT.image_choice
const CARD_GAP = 12
/** Two choices abreast: a picture is worth showing at a readable size. */
const COLUMNS = 2
/**
 * A choice's picture, as a share of its width.
 *
 * The card is a photograph with a caption, so the picture is sized from the *card*
 * rather than given a fixed height: a 110px band under a 300px card is a letterbox,
 * and under a 90px one it is a tower.
 */
const IMAGE_RATIO = 0.75

export function ImageChoiceSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  retryKey,
}: SlidePlayProps) {
  const p = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(24)
  // The caption under a choice wears the book's own hand.
  const answerText = usePlayOptionTextStyle()
  // A whole number of the page's rules between the question and its choices.
  const stackGap = usePlayStackGap(16)
  // Inside a book every selection affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  // Where the theme marks with a highlighter, the choice is marked on its
  // caption: the picture is a picture, and framing it in a tinted card is the
  // app talking over the page.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()
  /*
   * The grid's own width, measured — not the window's.
   *
   * The cards used to be sized from `Dimensions.get('window').width` and computed
   * once at module load. Inside a book that is simply wrong: the play surface is a
   * 9:16 frame in the middle of a much wider window, so the cards came out sized for
   * the window and were then clipped by the frame — a two-card row printed as one
   * oversized picture running off the page, with its caption cut off with it. Same
   * failure on a phone whose window turns, since a module-load constant never
   * re-measures. The grid knows its own width, so it reports it and the cards follow.
   */
  const [gridW, setGridW] = useState(0)
  const cardWidth = gridW > 0 ? (gridW - CARD_GAP * (COLUMNS - 1)) / COLUMNS : 0
  const imageHeight = cardWidth > 0 ? Math.round(cardWidth * IMAGE_RATIO) : 0
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)
  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelectedId(null)
  }

  const options = slide.options ?? []
  const solutionText = options.find(o => o.isCorrect)?.label

  const getCardBorderColor = (optId: string, isCorrect: boolean) => {
    if (highlight) return 'transparent'
    if (showFeedback) {
      if (isCorrect) return p.positive
      if (selectedId === optId && !isCorrect) return p.negative
    }
    if (selectedId === optId) return accent
    return p.border
  }

  /** The ink the captioned choice wears, or nothing when it is unmarked. */
  const markFor = (optId: string, isCorrect: boolean) => {
    if (!highlight) return null
    if (showFeedback) {
      if (isCorrect) return p.positive
      return selectedId === optId ? p.negative : null
    }
    return selectedId === optId ? accent : null
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={[ss.scrollContent, {padding, gap: stackGap}]}>
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>

        <View
          style={ss.grid}
          onLayout={e => {
            const w = Math.round(e.nativeEvent.layout.width)
            setGridW(prev => (prev === w ? prev : w))
          }}>
          {options.map(opt => {
            const borderColor = getCardBorderColor(opt.id, opt.isCorrect)
            return (
              <TouchableOpacity
                key={opt.id}
                style={[
                  ss.card,
                  {
                    borderColor,
                    // Zero until the grid has been measured: a card drawn at an
                    // assumed width is a card drawn at the window's width inside
                    // the book's, which is the bug this replaces.
                    ...(cardWidth > 0 ? {width: cardWidth} : null),
                    backgroundColor: highlight ? 'transparent' : p.surface,
                  },
                ]}
                onPress={() => {
                  if (showFeedback) return
                  setSelectedId(opt.id)
                  onAnswer({
                    isCorrect: opt.isCorrect,
                    points: opt.isCorrect ? 1 : 0,
                    maxPoints: 1,
                    solutionText,
                    explanation: slide.explanation,
                    userAnswer: opt.id,
                  })
                }}
                accessibilityRole="button"
                accessibilityLabel={opt.label}
                accessibilityHint={undefined}>
                {opt.imageUrl ? (
                  <Image
                    source={{uri: opt.imageUrl}}
                    style={[ss.cardImage, {height: imageHeight}]}
                    resizeMode="cover"
                    accessibilityIgnoresInvertColors
                  />
                ) : (
                  <View
                    style={[
                      ss.cardPlaceholder,
                      {backgroundColor: p.surfaceAlt, height: imageHeight},
                    ]}>
                    <Text style={[ss.cardPlaceholderText, {color: p.muted}]}>
                      {opt.label.slice(0, 1).toUpperCase()}
                    </Text>
                  </View>
                )}
                <Text
                  style={[ss.cardLabel, answerText, {color: p.body}]}
                  numberOfLines={2}>
                  <Text style={ink(markFor(opt.id, opt.isCorrect))}>
                    {opt.label}
                  </Text>
                </Text>
              </TouchableOpacity>
            )
          })}
        </View>
      </PlayScroller>
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  scrollContent: {padding: 24, gap: 16},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: CARD_GAP,
    marginTop: 4,
    /*
     * Full width, and *never* sized by its contents: the grid reports its own width
     * and the cards are then cut to fit it, so a container that took its width from
     * its children would be a loop — and, on the first paint, a grid with no width.
     */
    alignSelf: 'stretch',
    width: '100%',
  },
  card: {
    borderRadius: 12,
    borderWidth: 1.5,
    backgroundColor: PLAY_SURFACE.surface,
    // A caption longer than the card is clipped rather than printed across it.
    overflow: 'hidden',
  },
  cardImage: {
    width: '100%',
  },
  cardPlaceholder: {
    width: '100%',
    backgroundColor: PLAY_SURFACE.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardPlaceholderText: {
    fontSize: 32,
    fontWeight: '700',
    color: PLAY_SURFACE.textFaint,
  },
  cardLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: PLAY_SURFACE.text,
    padding: 10,
  },
  explanationBox: {
    padding: 14,
    backgroundColor: PLAY_SURFACE.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
  },
  explanationText: {
    fontSize: 14,
    color: PLAY_SURFACE.textMuted,
    lineHeight: 20,
  },
})
